import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ChevronLeft, Eye, Building2, Paperclip } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavbarStatusContext } from '../../contexts/NavbarStatusContext';
import supportChatService from '../../services/supportChatService';
import { format, isToday, isYesterday } from 'date-fns';

const POLL_INTERVAL = 5000; // 5 seconds when open

// --- Linkify helper: converts URLs in text to clickable <a> tags ---
function linkifyText(text) {
  if (!text) return text;
  const parts = text.split(/(https?:\/\/[^\s<]+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">
        {part}
      </a>
    ) : (
      part
    )
  );
}

// --- Avatar helper ---
function UserAvatar({ firstName, lastName, avatarUrl, isSuperAdmin, size = 'sm' }) {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`;

  if (avatarUrl) {
    return (
      <div className={`relative flex-shrink-0`}>
        <img
          src={avatarUrl}
          alt={`${firstName} ${lastName}`}
          className={`${sizeClasses} rounded-full object-cover`}
        />
        {isSuperAdmin && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white" title="JustPractice Support" />
        )}
      </div>
    );
  }

  return (
    <div className={`relative flex-shrink-0`}>
      <div
        className={`${sizeClasses} rounded-full flex items-center justify-center font-semibold ${
          isSuperAdmin
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-gray-200 text-gray-600'
        }`}
      >
        {initials}
      </div>
      {isSuperAdmin && (
        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white" title="JustPractice Support" />
      )}
    </div>
  );
}

// --- Message timestamp formatting ---
function formatMessageTime(dateStr) {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return `Yesterday ${format(date, 'h:mm a')}`;
  return format(date, 'MMM d, h:mm a');
}

// --- Date separator ---
function formatDateSeparator(dateStr) {
  const date = new Date(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
}

function shouldShowDateSeparator(current, previous) {
  if (!previous) return true;
  const a = new Date(current).toDateString();
  const b = new Date(previous).toDateString();
  return a !== b;
}

// --- Firm list for super admin ---
function FirmList({ firms, onSelectFirm }) {
  if (firms.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm p-4">
        No active support chats yet
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {firms.map((firm) => (
        <button
          key={firm.firm_id}
          onClick={() => onSelectFirm(firm)}
          className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-sm text-gray-900 truncate">{firm.firm_name}</span>
            </div>
            {firm.unread_count > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full flex-shrink-0">
                {firm.unread_count > 9 ? '9+' : firm.unread_count}
              </span>
            )}
          </div>
          {firm.last_message && (
            <p className="mt-1 text-xs text-gray-500 truncate">
              <span className="font-medium">{firm.last_message_user}:</span>{' '}
              {firm.last_message}
            </p>
          )}
          {firm.last_message_at && (
            <p className="mt-0.5 text-xs text-gray-400">
              {formatMessageTime(firm.last_message_at)}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}

// --- Chat messages view ---
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

function ChatView({ firmId, firmName, isSuperAdmin, currentUserId, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback((force = false) => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (force || isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: force ? 'auto' : 'smooth' });
    }
  }, []);

  const fetchMessages = useCallback(async (isInitial = false) => {
    try {
      const res = await supportChatService.getMessages(firmId);
      setMessages(res.data);
      if (isInitial) {
        setTimeout(() => scrollToBottom(true), 50);
      } else {
        scrollToBottom(false);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [firmId, scrollToBottom]);

  useEffect(() => {
    setLoading(true);
    fetchMessages(true);

    // Mark as read
    supportChatService.markAsRead(isSuperAdmin ? firmId : null).catch(() => {});

    // Poll for new messages
    pollRef.current = setInterval(() => {
      fetchMessages(false);
      supportChatService.markAsRead(isSuperAdmin ? firmId : null).catch(() => {});
    }, POLL_INTERVAL);

    return () => clearInterval(pollRef.current);
  }, [firmId, fetchMessages, isSuperAdmin]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text && !pendingImage) return;
    if (sending) return;

    setSending(true);
    try {
      let res;
      if (pendingImage) {
        res = await supportChatService.sendImageMessage(
          pendingImage.file,
          text,
          isSuperAdmin ? firmId : null
        );
        URL.revokeObjectURL(pendingImage.previewUrl);
        setPendingImage(null);
      } else {
        res = await supportChatService.sendMessage(text, isSuperAdmin ? firmId : null);
      }
      setMessages((prev) => [...prev, res.data]);
      setNewMessage('');
      setTimeout(() => scrollToBottom(true), 50);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert('Only image files (PNG, JPEG, GIF, WebP) are supported.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      alert('Image is too large. Maximum size is 10MB.');
      return;
    }
    if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage({ file, previewUrl: URL.createObjectURL(file) });
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleImageFile(file);
        return;
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
    e.target.value = '';
  };

  const clearPendingImage = () => {
    if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  return (
    <>
      {/* Header */}
      {isSuperAdmin && onBack && (
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
          <button onClick={onBack} className="p-1 hover:bg-gray-200 rounded transition-colors">
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900 truncate">{firmName}</span>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-2">
            <MessageCircle className="h-8 w-8" />
            <p>No messages yet</p>
            <p className="text-xs">Start a conversation with our support team</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwnMessage = msg.user_id === currentUserId;
            const prevMsg = idx > 0 ? messages[idx - 1] : null;
            const showDate = shouldShowDateSeparator(msg.created_at, prevMsg?.created_at);
            // Collapse consecutive messages from same user
            const showAvatar = !prevMsg || prevMsg.user_id !== msg.user_id || showDate;

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="flex items-center gap-2 py-2">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-xs text-gray-400 font-medium">
                      {formatDateSeparator(msg.created_at)}
                    </span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                )}
                {msg.is_internal_note ? (
                  <div className={`flex items-start gap-2 ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
                    <div className="flex-1 flex justify-center">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 italic">
                        <Eye className="h-3 w-3" />
                        <span className="font-medium">{msg.user_first_name}:</span> {linkifyText(msg.message)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`flex items-end gap-2 ${isOwnMessage ? 'flex-row-reverse' : ''} ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
                    {showAvatar ? (
                      <UserAvatar
                        firstName={msg.user_first_name}
                        lastName={msg.user_last_name}
                        avatarUrl={msg.user_avatar_url}
                        isSuperAdmin={msg.is_super_admin}
                      />
                    ) : (
                      <div className="w-8 flex-shrink-0" />
                    )}
                    <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                      {showAvatar && (
                        <p className={`text-xs font-medium mb-0.5 ${isOwnMessage ? 'text-right' : ''} ${
                          msg.is_super_admin ? 'text-yellow-700' : 'text-gray-500'
                        }`}>
                          {msg.user_first_name} {msg.user_last_name}
                          {msg.is_super_admin && <span className="ml-1 text-yellow-600">(Support)</span>}
                        </p>
                      )}
                      <div
                        className={`px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
                          isOwnMessage
                            ? 'bg-yellow-500 text-white rounded-br-md'
                            : 'bg-gray-100 text-gray-900 rounded-bl-md'
                        }`}
                      >
                        {msg.image_url && (
                          <a href={msg.image_url} target="_blank" rel="noopener noreferrer" className="block mb-1">
                            <img
                              src={msg.image_url}
                              alt="Shared attachment"
                              className="max-w-full max-h-48 rounded-lg cursor-pointer"
                              loading="lazy"
                            />
                          </a>
                        )}
                        {msg.message && msg.message !== '[image]' && linkifyText(msg.message)}
                      </div>
                      <p className={`text-xs text-gray-400 mt-0.5 ${isOwnMessage ? 'text-right' : ''}`}>
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="px-3 py-2 border-t border-gray-200 bg-white">
        {pendingImage && (
          <div className="mb-2 relative inline-block">
            <img
              src={pendingImage.previewUrl}
              alt="Preview"
              className="max-h-24 rounded-lg border border-gray-200"
            />
            <button
              type="button"
              onClick={clearPendingImage}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center text-xs hover:bg-gray-900 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Attach image"
            disabled={sending}
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
            onPaste={handlePaste}
            placeholder={pendingImage ? 'Add a caption (optional)...' : 'Type a message...'}
            rows={2}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
            style={{ maxHeight: '120px', overflowY: 'auto' }}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={(!newMessage.trim() && !pendingImage) || sending}
            className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </>
  );
}

// --- Main Widget ---
function SupportChatWidget() {
  const { user, isAuthenticated } = useAuth();
  const { supportChatEnabled: externalEnabled, supportChatUnreadCount: externalUnreadCount } = useNavbarStatusContext();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [enabled, setEnabled] = useState(null); // null = loading, true/false = resolved
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const widgetRef = useRef(null);

  // Check if user has Super Admin role
  useEffect(() => {
    if (!user) return;
    const roles = user.roles || user.active_roles?.map((r) => r.role_name) || [];
    setIsSuperAdmin(roles.includes('Super Admin'));
  }, [user]);

  // Sync enabled and unread count from consolidated navbar status endpoint
  useEffect(() => {
    if (externalEnabled !== undefined) {
      setEnabled(externalEnabled);
    }
  }, [externalEnabled]);

  useEffect(() => {
    if (!isOpen) {
      setUnreadCount(externalUnreadCount || 0);
    }
  }, [externalUnreadCount, isOpen]);

  // Load firm list for super admins when opening
  useEffect(() => {
    if (!isOpen || !isSuperAdmin) return;

    const loadFirms = async () => {
      try {
        const res = await supportChatService.getFirmChats();
        setFirms(res.data);
      } catch {
        // ignore
      }
    };

    loadFirms();
  }, [isOpen, isSuperAdmin]);

  const handleOpen = () => {
    setIsOpen(true);
    setUnreadCount(0);
    // Non-super-admin users go straight to their firm's chat
    if (!isSuperAdmin) {
      setSelectedFirm(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFirm(null);
  };

  const handleSelectFirm = (firm) => {
    setSelectedFirm(firm);
  };

  const handleBack = () => {
    setSelectedFirm(null);
    // Refresh firm list
    supportChatService.getFirmChats().then((res) => setFirms(res.data)).catch(() => {});
  };

  if (!isAuthenticated || !user || !enabled) return null;

  return (
    <div ref={widgetRef} className="fixed bottom-5 right-5 z-50">
      {/* Chat panel */}
      {isOpen && (
        <div className="absolute bottom-14 right-0 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#3A3A3A] text-white rounded-t-xl">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-yellow-400" />
              <h3 className="font-semibold text-sm">Support Chat</h3>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content area */}
          {isSuperAdmin && !selectedFirm ? (
            <FirmList firms={firms} onSelectFirm={handleSelectFirm} />
          ) : (
            <ChatView
              firmId={selectedFirm?.firm_id || user.active_firm_id}
              firmName={selectedFirm?.firm_name || ''}
              isSuperAdmin={isSuperAdmin}
              currentUserId={user.id}
              onBack={isSuperAdmin ? handleBack : null}
            />
          )}
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={isOpen ? handleClose : handleOpen}
        className={`relative inline-flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all ${
          isOpen
            ? 'bg-[#3A3A3A] text-white hover:bg-gray-700'
            : 'bg-yellow-500 text-white hover:bg-yellow-600 hover:shadow-xl'
        }`}
        aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
      >
        {isOpen ? (
          <X className="h-5 w-5" />
        ) : (
          <MessageCircle className="h-5 w-5" />
        )}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
}

export default SupportChatWidget;
